import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, ArrowRight, ArrowLeft, X, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RELEASE_ID, stepsFor } from "./releases";

const PAD = 8;
const keyFor = (page) => `${RELEASE_ID}:${page}`;
const localKey = (uid, page) => `novatus_whatsnew_${uid}_${keyFor(page)}`;
const findEl = (sel) => (sel ? [...document.querySelectorAll(sel)].find((e) => e.offsetParent !== null) : null);

// Ziel-Rechteck des hervorgehobenen Elements (folgt Scroll/Resize)
function useTargetRect(selector, active) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!active) return undefined;
    const el = findEl(selector);
    if (!el) { setRect(null); return undefined; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const upd = () => setRect(el.getBoundingClientRect());
    upd();
    const t = setTimeout(upd, 350);
    window.addEventListener("resize", upd);
    window.addEventListener("scroll", upd, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", upd); window.removeEventListener("scroll", upd, true); };
  }, [selector, active]);
  return rect;
}

function Spotlight({ rect }) {
  if (!rect) return <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />;
  return (
    <div className="fixed rounded-xl ring-2 ring-[#00E575] pointer-events-none transition-[top,left,width,height] duration-300"
         data-testid="whats-new-spotlight"
         style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.72), 0 0 24px 4px rgba(0,229,117,0.45)" }} />
  );
}

function cardStyle(rect) {
  if (!rect) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  const w = Math.min(360, window.innerWidth - 24);
  const left = Math.max(12, Math.min(rect.left + rect.width / 2 - w / 2, window.innerWidth - w - 12));
  const below = rect.bottom + 200 < window.innerHeight;
  return below ? { top: rect.bottom + PAD + 12, left, width: w } : { bottom: window.innerHeight - rect.top + PAD + 12, left, width: w };
}

function TourCard({ step, idx, total, rect, onNext, onBack, onClose }) {
  const last = idx === total - 1;
  return (
    <div className="fixed rounded-2xl border border-[#00E575]/40 bg-[#0F1014]/95 backdrop-blur-xl shadow-2xl p-4 w-[calc(100vw-24px)] max-w-[360px] animate-in fade-in zoom-in-95"
         style={cardStyle(rect)} data-testid="whats-new-card" role="dialog">
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#00E575]">
          <Sparkles size={13} /> Neu · {idx + 1}/{total}
        </span>
        <button type="button" onClick={onClose} data-testid="whats-new-close" title="Tour beenden"
                className="h-7 w-7 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 flex items-center justify-center"><X size={15} /></button>
      </div>
      <div className="text-[15px] font-bold text-zinc-50" data-testid="whats-new-title">{step.title}</div>
      <p className="text-[13px] text-zinc-300 leading-relaxed mt-1">{step.body}</p>
      {step.where && !rect && (
        <div className="mt-2 inline-flex items-start gap-1.5 text-[12px] text-sky-300" data-testid="whats-new-where">
          <MapPin size={13} className="mt-0.5 shrink-0" /> {step.where}
        </div>
      )}
      <div className="flex gap-2 mt-4">
        {idx > 0 && (
          <button type="button" onClick={onBack} data-testid="whats-new-back"
                  className="h-10 px-3 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-200 text-[13px] font-semibold inline-flex items-center gap-1"><ArrowLeft size={14} /> Zurück</button>
        )}
        <button type="button" onClick={onNext} data-testid="whats-new-next"
                className="h-10 flex-1 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[13.5px] inline-flex items-center justify-center gap-1 active:scale-[0.98] transition-transform">
          {last ? "Verstanden" : <>Weiter <ArrowRight size={14} /></>}
        </button>
      </div>
      {!last && (
        <button type="button" onClick={onClose} data-testid="whats-new-skip" className="mt-2 text-[12px] text-zinc-500 hover:text-zinc-300">Überspringen</button>
      )}
    </div>
  );
}

// Geführte "Was ist neu?"-Tour. page="global" (Dashboard) oder "draw" (Zeichen-Werkzeuge).
export function WhatsNewTour({ page = "global" }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [steps, setSteps] = useState(null);
  const [idx, setIdx] = useState(0);
  const step = steps?.[idx];
  const rect = useTargetRect(step?.selector, !!step);

  const start = useCallback(() => {
    // Schritte mit Ziel-Element nur zeigen, wenn es auf dieser Seite sichtbar ist
    const list = stepsFor(page, user?.role).filter((s) => !s.selector || findEl(s.selector));
    if (list.length) { setIdx(0); setSteps(list); }
  }, [page, user?.role]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const seen = (user.whats_new_seen || []).includes(keyFor(page)) || localStorage.getItem(localKey(user.id, page));
    const onPage = page !== "global" || pathname === "/dashboard";
    if (seen || !onPage) return undefined;
    const t = setTimeout(start, 1200);
    return () => clearTimeout(t);
  }, [user?.id, page, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (page !== "global") return undefined;
    window.addEventListener("novatus:whats-new-open", start);
    return () => window.removeEventListener("novatus:whats-new-open", start);
  }, [page, start]);

  const close = () => {
    setSteps(null);
    localStorage.setItem(localKey(user.id, page), "1");
    api.post("/users/me/whats-new-seen", { key: keyFor(page) }).catch(() => {});
  };

  if (!step) return null;
  return (
    <div className="fixed inset-0 z-[120]" data-testid="whats-new-tour">
      <Spotlight rect={rect} />
      <TourCard step={step} idx={idx} total={steps.length} rect={rect}
                onNext={() => (idx === steps.length - 1 ? close() : setIdx(idx + 1))}
                onBack={() => setIdx(Math.max(0, idx - 1))} onClose={close} />
    </div>
  );
}

export default WhatsNewTour;
